import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconFileText } from "@tabler/icons-react";
import { DesktopTopNav } from "../../../components/app/desktopTopNav/DesktopTopNav";
import { DesktopBreadcrumb } from "../../../components/app/desktopTopNav/DesktopBreadcrumb";
import { DesktopCollectionCardSkeleton } from "../../../components/cards/DesktopCollectionCard";
import {
  RailSectionSkeleton,
  RailSectionTitle,
  YourRoomsSection,
} from "../../../components/discovery/DesktopExploreRail";
import { DesktopQuickComposer } from "../../../components/gpt/DesktopQuickComposer";
import { Button } from "../../../components/ui/Button";
import { Tag } from "../../../components/ui/Tag";
import { useEffectOnce } from "../../../domains/app/hooks/useEffectOnce";
import { usePageLinks } from "../../../domains/app/hooks/usePageLinks";
import { goBackInApp } from "../../../domains/app/utils/backNavigation";
import { getItemTranslation } from "../../../domains/app/utils/getItemTranslation";
import { useFeedScrollMemory } from "../../../domains/app/stores/scrollPositionsStore";
import { SaveableCollectionCard } from "../../../domains/playlist/components/SaveableCollectionCard";
import {
  seedPlaylistDetailCache,
  useGetPersonalRecommendedPlaylistsQuery,
  type ApiPlaylistModel,
} from "../../../domains/playlist/hooks/useGetRecommendedPlaylistsQuery";
import { usePracticeArm } from "../../../domains/practice/hooks/usePracticeArm";
import { useApiQuery } from "../../../services/api";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import {
  gaEventSelectItemPlaylist,
  gaEventViewItemListPlaylist,
} from "../../../tools/analytics/domains/playlist.analytics";
import {
  WHOLE_PRACTICE,
  buildSelectionSearch,
} from "../../practice-feed/practiceFeedSelection";

/**
 * "Collections for you" on a desktop browser (Figma "D3.explore.collections"),
 * the "See all" of the Explore collections band: every collection ranked for
 * the reader, two to a row, under a header that says what they are ranked on.
 *
 * Same ranking as the band (`/webapp/private/playlists/recommended`), so the
 * page opens on the collections the band showed. Its rail names what the
 * ranking reads: the reader's practices, then the rooms they follow.
 */
export function DesktopCollectionsForYouPage(): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const collections = useGetPersonalRecommendedPlaylistsQuery(null);
  const { basis, practices } = useRankingBasis();
  const { publicPlaylistLink } = usePageLinks(null);
  const { scrollRef, rememberScroll } = useFeedScrollMemory<HTMLElement>(
    "/discovery/playlists",
    collections.playlists.length > 0
  );

  // The same list events as the collections band, under the same list name.
  useEffectOnce(() => {
    if (collections.playlists.length > 0) {
      gaEventViewItemListPlaylist({
        playlists: collections.playlists,
        listName: AnalyticsItemList.DISCOVERY_PLAYLISTS,
      });
    }
  }, [collections.playlistIds]);

  function openPlaylist(playlist: ApiPlaylistModel): void {
    // The ranked doc is the full detail shape: it paints the playlist header.
    seedPlaylistDetailCache(playlist);
    gaEventSelectItemPlaylist({
      playlist,
      listName: AnalyticsItemList.DISCOVERY_PLAYLISTS,
    });
    history.push({
      pathname: publicPlaylistLink(playlist),
      state: { playlist },
    });
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <DesktopTopNav
        context={
          <DesktopBreadcrumb
            back={{
              kind: "first-crumb",
              onBack: () => {
                goBackInApp(history, "/discovery");
              },
            }}
            trail={[{ label: t("Explore"), to: "/discovery" }]}
            current={t("Collections")}
          />
        }
        trailing={{ kind: "search" }}
        surface="default"
        className={null}
      />
      <div className="relative flex min-h-0 flex-1 overflow-hidden">
        <main
          ref={scrollRef}
          className="min-w-0 flex-1 overflow-y-auto"
          onScroll={rememberScroll}
        >
          <div className="px-10 pt-12 pb-10">
            <div className="mx-auto flex max-w-desktop-block gap-8">
              <div className="flex min-w-0 max-w-desktop-column flex-1 flex-col gap-12">
                <header className="flex max-w-155 flex-col gap-3">
                  {basis === null ? null : (
                    <p className="m-0 text-footnote text-ds-slate-700">
                      {basis === "practices"
                        ? t(
                            "Explore · based on your practices and the rooms you follow"
                          )
                        : t(
                            "Explore · based on your interests and the rooms you follow"
                          )}
                    </p>
                  )}
                  <h1 className="m-0 text-desktop-title font-semibold text-ds-black">
                    {t("Collections for you")}
                  </h1>
                  {basis === null ? null : (
                    <p className="m-0 text-desktop-body text-ds-slate-700">
                      {basis === "practices"
                        ? t(
                            "Curated collections of articles, picked for your practices and the rooms you follow."
                          )
                        : t(
                            "Curated collections of articles, picked for your interests and the rooms you follow."
                          )}{" "}
                      {t("New collections every week.")}
                    </p>
                  )}
                  {collections.total === null ? null : (
                    <div className="flex items-center gap-1.5 text-footnote text-ds-slate-700">
                      <IconFileText size={16} stroke={1.5} />
                      <span>
                        {[
                          t("{{count}} collections", {
                            count: collections.total,
                          }),
                          ...(practices.length > 0
                            ? [
                                t("{{count}} practices", {
                                  count: practices.length,
                                }),
                              ]
                            : []),
                        ].join(" · ")}
                      </span>
                    </div>
                  )}
                </header>

                <section className="flex flex-col gap-6">
                  {collections.total === null ? null : (
                    <div className="flex items-baseline gap-2 pb-2 text-ds-black">
                      <h2 className="m-0 text-heading-lg font-semibold">
                        {t("{{count}} collections", {
                          count: collections.total,
                        })}
                      </h2>
                      <span className="text-footnote">
                        {t("sorted by relevance")}
                      </span>
                    </div>
                  )}
                  {collections.state === "error" ? (
                    <div className="flex flex-col items-center gap-2 py-12 text-center">
                      <p className="m-0 text-sm text-ds-slate-500">
                        {t("The collections couldn't load.")}
                      </p>
                      <Button
                        type="button"
                        variant="secondary"
                        size="md"
                        className="mt-2 text-footnote font-medium"
                        disabled={collections.isFetching}
                        onClick={collections.retry}
                      >
                        {t("Try again")}
                      </Button>
                    </div>
                  ) : collections.isLoading ? (
                    <div className="grid grid-cols-2 gap-8">
                      {Array.from({ length: 6 }).map((_, idx) => (
                        <DesktopCollectionCardSkeleton key={idx} />
                      ))}
                    </div>
                  ) : collections.playlists.length === 0 ? (
                    <p className="m-0 py-6 text-center text-footnote text-ds-slate-500">
                      {t("No content to show right now.")}
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 gap-8">
                      {collections.playlists.map((playlist) => (
                        <SaveableCollectionCard
                          key={playlist.id}
                          playlist={playlist}
                          listName={AnalyticsItemList.DISCOVERY_PLAYLISTS}
                          onOpen={() => {
                            openPlaylist(playlist);
                          }}
                        />
                      ))}
                    </div>
                  )}
                  {collections.canFetchNextPage ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="self-center"
                      disabled={collections.isFetching}
                      onClick={collections.fetchNextPage}
                    >
                      {t("Load more")}
                    </Button>
                  ) : null}
                </section>
              </div>
              <div className="hidden w-desktop-rail shrink-0 border-l border-ds-slate-100 px-6 lg:block">
                <div className="sticky top-8">
                  <CollectionsRail practices={practices} />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <DesktopQuickComposer />
    </div>
  );
}

type ReaderPractice = { uid: string; name: string };

/**
 * What the ranking reads besides the rooms: the practices for a reader in the
 * practice arm, the interests for the others, or not known yet while the arm
 * is pending. The practices the reader holds are listed once their catalogue
 * is in, and none before: the basis, not the list, picks the header's words.
 */
function useRankingBasis(): {
  basis: "practices" | "interests" | null;
  practices: ReadonlyArray<ReaderPractice>;
} {
  const arm = usePracticeArm();
  const catalogue = useApiQuery(
    "/webapp/private/practice-profiles",
    arm === "in" ? {} : false
  );
  const practices =
    catalogue.state === "success"
      ? catalogue.body.docs.map((practice) => ({
          uid: practice.uid,
          name: getItemTranslation(practice),
        }))
      : [];
  switch (arm) {
    case "pending":
      return { basis: null, practices: [] };
    case "out":
      return { basis: "interests", practices: [] };
    case "in":
      return { basis: "practices", practices };
    default:
      throw new Error(arm satisfies never);
  }
}

/**
 * The rail of the page: the reader's practices, each a way to its feed, then
 * the rooms they follow.
 */
function CollectionsRail({
  practices,
}: {
  practices: ReadonlyArray<ReaderPractice>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const joinedRoomsQuery = useApiQuery("/webapp/private/rooms", {});

  return (
    <aside className="flex w-full flex-col gap-10 pt-2">
      {practices.length === 0 ? null : (
        <section className="flex flex-col gap-4">
          <RailSectionTitle title={t("Your practices")} onSeeAll={null} />
          <div className="flex flex-wrap gap-1.5">
            {practices.map((practice) => (
              <Link
                key={practice.uid}
                to={`/discovery${buildSelectionSearch({
                  corpus: { kind: "practice", uid: practice.uid },
                  mode: WHOLE_PRACTICE,
                })}`}
                className="max-w-full no-underline"
              >
                <Tag
                  variant="slate"
                  size="sm"
                  state="idle"
                  icon={null}
                  className="max-w-full text-footnote hover:bg-ds-slate-200"
                >
                  <span className="truncate">{practice.name}</span>
                </Tag>
              </Link>
            ))}
          </div>
        </section>
      )}
      {joinedRoomsQuery.state === "pending" ? (
        <RailSectionSkeleton />
      ) : joinedRoomsQuery.state === "success" &&
        joinedRoomsQuery.body.rooms.length > 0 ? (
        <YourRoomsSection rooms={joinedRoomsQuery.body.rooms} />
      ) : null}
    </aside>
  );
}
